import React, { useState, useMemo, useEffect, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { functions } from "../../../../constants/common-functions";
import UsageInspectorPopUp from "./UsageInspectorPopUp";
import UsageInspectorDayDrawer from "./UsageInspectorDayDrawer";
import {
    faChevronRight,
    faChevronDown,
    faUser,
    faUsers,
    faMap
} from "@fortawesome/free-solid-svg-icons";

import { useHistory } from "react-router-dom";

import {
    METRIC_UTILISATION_CONFIG
} from "../../../../constants/metric-config";


import {store} from '../../../../context/store';

export default function ProgressGrid({
    floors,
    dates,
    onDeskClick,
    workplaceActivities,
    metric
}) {

    const history = useHistory();
	
	const globalState = useContext(store);

    const [expandedFloors, setExpandedFloors] = useState({});
    const [expandedSpaces, setExpandedSpaces] = useState({});
    const [selectedDeskId, setSelectedDeskId] = useState(null);

    const [hoverData, setHoverData] = useState(null);
    const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
    const [hoveredSpaceId, setHoveredSpaceId] = useState(null);

    const [activeFloor, setActiveFloor] = useState(null);

    const [hoverCache, setHoverCache] = useState({});
	const [selectedDay, setSelectedDay] = useState(null);
	const [drawerOpen, setDrawerOpen] = useState(false);

    // --------------------------------------------------------
    // NEW
    // --------------------------------------------------------

    const [loadedFloorSpaces, setLoadedFloorSpaces] =
        useState({});

    const [loadingFloors, setLoadingFloors] =
        useState({});
		
		
	var metricConfig =
		METRIC_UTILISATION_CONFIG[metric];
		
	const filterBean = {};
				
	const queryContext =
		    globalState?.state?.queryContext;

    // --------------------------------------------------------
    // CLICK OUTSIDE
    // --------------------------------------------------------

    useEffect(function () {

        function handleClickOutside() {

            setHoverData(null);
            setHoveredSpaceId(null);

        }

        if (hoverData) {

            window.addEventListener(
                "click",
                handleClickOutside
            );

        }

        return function () {

            window.removeEventListener(
                "click",
                handleClickOutside
            );

        };

    }, [hoverData]);
	
	
	useEffect(function () {
		
	    setLoadingFloors({});
		loadFloor();

	}, [metric, queryContext?.utilisationSurveyId]);

    // --------------------------------------------------------
    // LOAD FLOOR
    // --------------------------------------------------------

    function loadFloor(floorId) {

        setLoadingFloors(function (prev) {

            return {
                ...prev,
                [floorId]: true
            };

        });

        functions.postFilterBeanAjax(

            "/resolutionsoftwareplatform/usageinspector/floordetail" +
            "?floorId=" + floorId +
            "&metric=" + metric,
			functions.buildPostBodyFilterBeanQueryContext(filterBean, queryContext),
            function (response) {

                setLoadedFloorSpaces(function (prev) {

                    return {
                        ...prev,
                        [floorId]: response
                    };

                });

                setLoadingFloors(function (prev) {

                    return {
                        ...prev,
                        [floorId]: false
                    };

                });

            }

        );

    }

    // --------------------------------------------------------
    // FLOOR PLAN
    // --------------------------------------------------------

    const goFloor = function (id) {

        var surveyId = -100;

        window.location =
            "/floorplan/utilisation/" +
            id +
            "?surveyId=" +
            surveyId;

    };

    // --------------------------------------------------------
    // ACTIVITY MAP
    // --------------------------------------------------------

    const activityMap = useMemo(function () {

        const map = {};

        if (workplaceActivities?.length) {

            workplaceActivities.forEach(function (a) {

                map[a.id] = a;

            });

        }

        return map;

    }, [workplaceActivities]);

    // --------------------------------------------------------
    // RESOLVE ACTIVITY
    // --------------------------------------------------------

    function resolveActivity(record) {

        if (!record) {

            return {
                label: "No Data",
                background: "#475569",
                opacity: 0.5
            };

        }

        if (!record.activityId) {

            return {
                label: "No Activity",
                background: "#1e293b"
            };

        }

        const activity =
            activityMap[record.activityId];

        if (!activity) {

            return {
                label: "Unknown",
                background: "#6b7280"
            };

        }

        const colour =
            activity.color || activity.colour;

        return {
            label: activity.name,
            background:
                colour
                    ? colour + "CC"
                    : "#f97316"
        };

    }

    // --------------------------------------------------------
    // GUARDS
    // --------------------------------------------------------

    if (!floors || !dates) {
        return null;
    }

    // --------------------------------------------------------
    // DATE FORMAT
    // --------------------------------------------------------

    function formatUKDate(dateStr) {

        const d = new Date(dateStr);

        const day =
            String(d.getDate()).padStart(2, "0");

        const month =
            String(d.getMonth() + 1)
                .padStart(2, "0");

        return day + "/" + month;

    }

    // --------------------------------------------------------
    // VALUE HELPERS
    // --------------------------------------------------------

    function getValueForDate(days, date) {

        if (!days) {
            return 0;
        }

        const records = days.filter(function (d) {

            return d.date === date;

        });

        if (records.length === 0) {
            return 0;
        }

        const total =
            records.reduce(function (sum, r) {

                return sum + (
                    r.utilisation ??
                    r.avgUtilisation ??
                    r.utilisationPercentage ??
                    0
                );

            }, 0);

        return Math.round(
            total / records.length
        );

    }

    function getFloorValueForDate(
        floor,
        date
    ) {

        if (
            floor.days &&
            floor.days.length > 0
        ) {

            return getValueForDate(
                floor.days,
                date
            );

        }

        return 0;

    }

    // --------------------------------------------------------
    // METRIC COLOUR
    // --------------------------------------------------------

	function getMetricColour(metricCode) {

	    const config =
	        METRIC_UTILISATION_CONFIG[metricCode];

	    if (!config || !config.color) {

	        return "47,111,237";

	    }

	    const hex =
	        config.color.replace("#", "");

	    const r =
	        parseInt(hex.substring(0, 2), 16);

	    const g =
	        parseInt(hex.substring(2, 4), 16);

	    const b =
	        parseInt(hex.substring(4, 6), 16);

	    return r + "," + g + "," + b;

	}

    // --------------------------------------------------------
    // CELL STYLE
    // --------------------------------------------------------

    const getCellStyle = function (val) {

        const intensity =
            Math.min(val / 100, 1);

        const rgb =
            getMetricColour(metric);

        return {
            textAlign: "center",
            background:
                "rgba(" +
                rgb +
                ", " +
                intensity +
                ")",
            color:
                val > 60
                    ? "#fff"
                    : "#333",
            borderRadius: "4px",
            padding: "3px 0",
            fontSize: "12px",
            cursor: "pointer",
            transition: "all 0.15s ease"
        };

    };

    // --------------------------------------------------------
    // RENDER
    // --------------------------------------------------------

    return (
		<>
        <div>

            {/* HEADER */}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        `310px repeat(${dates.length}, 1fr)`,
                    fontWeight: "600",
                    borderBottom: "2px solid #ddd",
                    marginBottom: "10px",
                    background: "#fff",
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                    height: "42px",
                    alignItems: "center"
                }}
            >

                <div></div>

                {dates.map(function (d) {

                    return (

                        <div
                            key={d}
                            style={{
                                textAlign: "center",
                                fontSize: "12px",
                                borderRight:
                                    "1px solid #eee"
                            }}
                        >
                            {formatUKDate(d)}
                        </div>

                    );

                })}

            </div>

            {/* FLOORS */}

			
			
			
            {floors.map(function (
                floor,
                index
            ) {

                const open =
                    expandedFloors[floor.floorId];

                const loadedFloor =
                    loadedFloorSpaces[
                        floor.floorId
                    ];

                const spaces =
                    loadedFloor &&
                    loadedFloor.spaces
                        ? loadedFloor.spaces
                        : [];

                return (

                    <div key={floor.floorId}>

                        {/* FLOOR ROW */}
						
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    `310px repeat(${dates.length}, 1fr)`,
                                padding: "8px",
                                background:
                                    index % 2
                                        ? "#fafbfc"
                                        : "#fff",
                                borderLeft:
                                    "4px solid " + metricConfig.color
                            }}
                        >

                            <div

                                onClick={function () {

                                    const isCurrentlyOpen =
                                        !!expandedFloors[
                                            floor.floorId
                                        ];

                                    if (
                                        !isCurrentlyOpen &&
                                        !loadedFloorSpaces[
                                            floor.floorId
                                        ]
                                    ) {

                                        loadFloor(
                                            floor.floorId
                                        );

                                    }

                                    setExpandedFloors(function (prev) {

                                        return {
                                            ...prev,
                                            [floor.floorId]:
                                                !isCurrentlyOpen
                                        };

                                    });

                                    if (isCurrentlyOpen) {

                                        setActiveFloor(null);

                                    }
                                    else {

                                        setActiveFloor(floor);

                                    }

                                }}

                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    cursor: "pointer"
                                }}
                            >

                                {/* MAP */}

                                <FontAwesomeIcon
                                    icon={faMap}
                                    title="Open Floor Plan"
                                    onClick={function (e) {

                                        e.stopPropagation();

                                        goFloor(
                                            floor.floorId
                                        );

                                    }}
                                    style={{
                                        fontSize: "14px",
                                        color: metricConfig.color,
                                        cursor: "pointer"
                                    }}
                                />

                                {/* CHEVRON */}

                                <FontAwesomeIcon
                                    icon={
                                        open
                                            ? faChevronDown
                                            : faChevronRight
                                    }
                                    style={{
                                        fontSize: "16px",
                                        color: "#374151"
                                    }}
                                />

                                <span>
                                    {floor.floorName}
                                </span>

                            </div>

                            {dates.map(function (d) {

                                return (

                                    <div key={d}>

                                        <div
                                            style={{
                                                textAlign: "center",
                                                fontSize: "12px",
                                                color: "#333"
                                            }}
                                        >

                                            {getFloorValueForDate(
                                                floor,
                                                d
                                            )}%

                                        </div>

                                    </div>

                                );

                            })}

                        </div>

                        {/* LOADING */}

                        {open &&
                            loadingFloors[
                                floor.floorId
                            ] && (

                            <div
                                style={{
                                    padding:
                                        "12px 20px",
                                    fontSize: "12px",
                                    color: "#666"
                                }}
                            >
                                Loading floor...
                            </div>

                        )}

                        {/* SPACES */}

                        {open &&
                            spaces.map(function (space) {
								
                                const isMulti =
                                    space.isMeeting === true ||
                                    space.is_meeting === true ||
                                    space.capacity > 1;

                                return (
									
                                    <div key={space.spaceId}>

                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns:
                                                    `310px repeat(${dates.length}, 1fr)`,
                                                padding: "6px",
                                                background:
                                                    hoveredSpaceId ===
                                                    space.spaceId
                                                        ? "#e0f2fe"
                                                        : "#f4f6f8",
                                                borderLeft:
                                                    "4px solid #cbd5f5"
                                            }}
                                        >

                                            {/* SPACE LABEL */}

                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    marginLeft: "1px",
                                                    fontSize: "12px",
                                                    cursor: "pointer",
                                                    overflow: "hidden",
                                                    whiteSpace: "nowrap",
                                                    textOverflow:
                                                        "ellipsis"
                                                }}
                                            >

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        minWidth: "20px",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "space-around"
                                                    }}
													onClick={function () {

													       if (onDeskClick) {

													           onDeskClick({
													               ...space,
													               allDates: dates
													           });

													       }

													   }}
                                                >

                                                    <FontAwesomeIcon
                                                        icon={
                                                            isMulti
                                                                ? faUsers
                                                                : faUser
                                                        }
                                                        style={{
                                                            fontSize:
                                                                "12px",
                                                            color:
                                                                isMulti
                                                                    ? "#dc2626"
                                                                    : "#374151"
                                                        }}
                                                    />

                                                </div>

												<div
												    style={{
												        overflow: "hidden",
												        whiteSpace: "nowrap",
												        textOverflow: "ellipsis"
												    }}
												>
												    {space.code}

												    {
												        space.type
												        &&
												        " - " + space.type
												    }

												    {
												        space.organisation
												        &&
												        " - " + space.organisation
												    }
												</div>

                                            </div>

                                            {/* DATES */}

                                            {dates.map(function (d) {

                                                return (

                                                    <div
                                                        key={d}
                                                        style={{
                                                            paddingLeft:
                                                                "2px",
                                                            paddingRight:
                                                                "2px"
                                                        }}
                                                    >

                                                        <div
														onClick={function () {

														    if (hoverData) {

														        setSelectedDay({
														            ...hoverData,
														            _openedAt: Date.now()
														        });

														        setTimeout(function () {

														            setDrawerOpen(true);

														        }, 20);

														    }

														}}
                                                            onMouseEnter={function (e) {

                                                                setHoveredSpaceId(
                                                                    space.spaceId
                                                                );

                                                                const cacheKey =
                                                                    space.spaceId +
                                                                    "_" +
                                                                    d +
                                                                    "_" +
                                                                    metric;

                                                                const cached =
                                                                    hoverCache[
                                                                        cacheKey
                                                                    ];

                                                                // CACHE HIT

                                                                if (cached) {

                                                                    setHoverData(
                                                                        cached
                                                                    );

                                                                    setHoverPos({
                                                                        x: e.clientX,
                                                                        y: e.clientY
                                                                    });

                                                                    return;

                                                                }

                                                                // LOAD

                                                                functions.postFilterBeanAjax(

                                                                    "/resolutionsoftwareplatform/usageinspector/usageinspectordetail" +
                                                                    "?spaceId=" + space.spaceId +
                                                                    "&date=" + d +
                                                                    "&metric=" + metric,
																	functions.buildPostBodyFilterBeanQueryContext(filterBean, queryContext),
                                                                    function (res) {

                                                                        const dayData =
                                                                            res.find(function (r) {

                                                                                return r.date === d;

                                                                            });

                                                                        const hoverResult = {
                                                                            ...dayData,
                                                                            capacity:
                                                                                space.capacity,
                                                                            spaceCode:
                                                                                space.code
                                                                        };

                                                                        setHoverCache(function (prev) {

                                                                            return {
                                                                                ...prev,
                                                                                [cacheKey]:
                                                                                    hoverResult
                                                                            };

                                                                        });

                                                                        setHoverData(
                                                                            hoverResult
                                                                        );

                                                                        setHoverPos({
                                                                            x: e.clientX,
                                                                            y: e.clientY
                                                                        });

                                                                    }

                                                                );

                                                            }}

                                                            onMouseLeave={function () {

                                                                setHoverData(null);

                                                                setHoveredSpaceId(null);

                                                            }}

                                                            style={getCellStyle(
                                                                getValueForDate(
                                                                    space.days,
                                                                    d
                                                                )
                                                            )}

                                                        >

                                                            {getValueForDate(
                                                                space.days,
                                                                d
                                                            )}%

                                                        </div>

                                                    </div>

                                                );

                                            })}

                                        </div>

                                    </div>

                                );

                            })}

            </div>

                );

            })}

			{/* HOVER POPUP */}
			{hoverData && (() => {
			    return (
			        <UsageInspectorPopUp
			            hoverData={hoverData}
			            hoverPos={hoverPos}
			            resolveActivity={resolveActivity}
			        />
			    );
			})()}
			
        </div>
		<UsageInspectorDayDrawer
		    open={!!selectedDay}
		    data={selectedDay}
			onClose={function () {

			      setDrawerOpen(false);

			      setTimeout(function () {

			          setSelectedDay(null);

			      }, 350);

			  }}
		    resolveActivity={resolveActivity}
		/>
		</>
    );

}